import { useNavigate } from 'react-router-dom'
import { ChevronRight, Ticket } from 'lucide-react'
import { useMyExperienceTickets } from '../hooks/useMyExperienceTickets'
import { useI18n } from '../i18n/useI18n'

/**
 * ホームの「保有中の体験チケット」カード (#1285)。利用可能(issued)チケットを
 * 持っている時だけ表示し、当日その場で QR を出せる導線にする。
 * 将来は日付/位置で「当日・近く」だけ出す文脈依存に (今は保有時は常時表示)。
 * feature OFF / 未ログイン / 保有なし は null (何も出さない)。
 */
export default function HomeTicketsCard() {
  const navigate = useNavigate()
  const { t, locale } = useI18n()
  const { active } = useMyExperienceTickets()
  const held = active.filter((ti) => ti.status === 'issued')
  if (held.length === 0) return null

  const go = () =>
    held.length === 1 ? navigate(`/my-tickets/${held[0].id}`) : navigate('/my-reservations')
  const sub =
    held.length === 1
      ? (locale === 'ja' ? held[0].productName : held[0].productNameEn || held[0].productName)
      : t('experience.homeCardCount', { count: held.length })
  // #1522 送迎の状況は券を開かなくてもホームで分かるようにする。
  // 「向かっています」は到着予定時刻と一緒に出し、「到着」は集合場所を添える
  const moving = held.find((ti) => ti.transportIncluded && (ti.transportStatus === 'heading' || ti.transportStatus === 'arrived'))
  const transportLine = (() => {
    if (!moving) return null
    if (moving.transportStatus === 'arrived') {
      return t('experience.homeCardTransportArrived', { place: moving.meetingPoint?.name || t('experience.transportWaitingPlace') })
    }
    const eta = moving.transportEtaAt ? new Date(moving.transportEtaAt) : null
    const time = eta && !Number.isNaN(eta.getTime())
      ? eta.toLocaleTimeString(locale, { hour: '2-digit', minute: '2-digit' })
      : ''
    return time
      ? t('experience.homeCardTransportHeading', { name: moving.transportAssigneeName || '', time })
      : t('experience.homeCardTransportHeadingNoEta', { name: moving.transportAssigneeName || '' })
  })()

  return (
    <button
      onClick={go}
      className="tap-card animate-fade-in mx-4 mt-4 flex w-[calc(100%-2rem)] items-center gap-3 rounded-2xl bg-gradient-to-r from-gold/15 to-sakura/15 p-4 text-left shadow-card"
    >
      <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-gold text-white">
        <Ticket size={20} />
      </div>
      <div className="min-w-0 flex-1">
        <p className="font-display text-sm font-semibold text-text-primary">
          {t('experience.homeCardTitle')}
        </p>
        <p className="mt-0.5 truncate font-body text-xs text-text-secondary">{sub}</p>
        {transportLine && (
          <p className="mt-1 font-body text-xs font-semibold text-matcha">{transportLine}</p>
        )}
      </div>
      <span className="flex shrink-0 items-center gap-0.5 font-body text-[13px] font-medium text-yokoso-red">
        {t('experience.homeCardCta')}
        <ChevronRight size={16} />
      </span>
    </button>
  )
}
